import type { BylinePageAdf } from '../hooks/useBylinePageAdf.ts';

import {
	createAtAGlancePanelObservation,
	type AtAGlancePanelFields,
	type AtAGlanceObservedResult,
} from './atAGlancePanelObservation.ts';
import type { AtAGlanceFailureReason } from './atAGlanceAnalytics.ts';
import type { AtAGlanceRetryReason } from './atAGlanceRetry.ts';
import type { AtAGlanceOutput } from './atAGlanceTypes.ts';
import type { AtAGlanceAcceptedResult } from './atAGlanceResultCache.ts';
import {
	createAtAGlanceReuseObservation,
	type AtAGlanceReuseFields,
} from './atAGlanceReuseObservation.ts';

export type AtAGlanceCancellationReason =
	| 'navigation'
	| 'changedInput'
	| 'temporaryInputLoading'
	| 'componentRemoval'
	| 'unknown';

export type AtAGlanceRequestPhase = 'beforeAttempt' | 'retryDelay' | 'activeAttempt' | 'settled';

export type AtAGlanceBrowserDecision =
	| 'completedMatch'
	| 'pendingMatch'
	| 'newOperation'
	| 'waitingForInput'
	| 'unableToRequest'
	| 'unknown';

export type AtAGlanceSummaryStartTrigger = 'panel' | 'popup';

export type AtAGlanceExecutionObservation = {
	stage:
		| 'operationStarted'
		| 'attemptStarted'
		| 'attemptFinished'
		| 'retryWait'
		| 'operationFinished';
	phase: AtAGlanceRequestPhase;
	attemptCount: number;
	outcome?: 'succeeded' | 'failed' | 'aborted';
	reason?: AtAGlanceCancellationReason | AtAGlanceFailureReason | AtAGlanceRetryReason;
	statusCode?: number;
	parsedStatus?: AtAGlanceOutput['status'];
	delayMs?: number;
};

export type AtAGlanceBrowserObservation = Omit<Partial<AtAGlanceExecutionObservation>, 'stage'> &
	AtAGlancePanelFields &
	AtAGlanceReuseFields & {
		stage:
			| AtAGlanceExecutionObservation['stage']
			| 'popupOpened'
			| 'popupDecision'
			| 'visitEnded'
			| 'panelOpened'
			| 'panelState'
			| 'panelFinished';
		visitId: string;
		browserTimeMs: number;
		operationIndex?: number;
		popupOpeningIndex?: number;
		decision?: AtAGlanceBrowserDecision;
		replacementInput?: 'firstOperation' | 'identical' | 'changed';
		firstPopupOpenedAtMs?: number;
		firstAttemptStartedAtMs?: number;
		lastResponseCompletedAtMs?: number;
		summaryStartTrigger?: AtAGlanceSummaryStartTrigger;
	};

export type AtAGlanceObservationSnapshot = AtAGlanceReuseFields & {
	visitId?: string;
	popupOpeningCount: number;
	firstPopupOpenedAtMs?: number;
	firstAttemptStartedAtMs?: number;
	lastResponseCompletedAtMs?: number;
	phase: AtAGlanceRequestPhase;
	decision: AtAGlanceBrowserDecision;
};

export type AtAGlanceObservedInput = {
	requestBody: string | null;
	waitingOnPrereqs: boolean;
	enabled: boolean;
	completedMatch: boolean;
	documentOutcome?: BylinePageAdf['documentOutcome'];
	documentReady?: boolean;
};

export type AtAGlanceObservation = {
	observePanel: (open: boolean) => void;
	observeResult: (result: AtAGlanceObservedResult) => void;
	observeOverviewCommit: (acceptedResult?: AtAGlanceAcceptedResult) => void;
	observePopup: (open: boolean) => void;
	resolvePopupDecision: () => void;
	observeInput: (input: AtAGlanceObservedInput) => void;
	startOperation: (requestBody: string) => (event: AtAGlanceExecutionObservation) => void;
	getSnapshot: () => AtAGlanceObservationSnapshot;
	getCancellationReason: (latestInput?: {
		requestBody: string | null;
		waitingOnPrereqs: boolean;
	}) => AtAGlanceCancellationReason;
	endVisit: () => void;
};

// https://hello.atlassian.net/browse/CCINT-9575: popup activity and operation outcomes need one visit scope. The existing
// app-instance trace spans navigation and cannot establish popup-only demand or replacement work.
export const createAtAGlanceObservation = ({
	emit,
	getVisitEndReason,
	getSummaryStartTrigger,
	now = () => Math.round(performance.now()),
	createId = () => crypto.randomUUID(),
}: {
	emit: (event: AtAGlanceBrowserObservation) => void;
	getVisitEndReason: () => AtAGlanceCancellationReason;
	getSummaryStartTrigger?: () => AtAGlanceSummaryStartTrigger | undefined;
	now?: () => number;
	createId?: () => string;
}): AtAGlanceObservation => {
	let visitId: string | undefined;
	let ended = false;
	let popupOpen = false;
	let popupOpeningCount = 0;
	let pendingPopupOperationIndex: number | undefined;
	let firstPopupOpenedAtMs: number | undefined;
	let firstAttemptStartedAtMs: number | undefined;
	let lastResponseCompletedAtMs: number | undefined;
	let operationIndex = 0;
	let activeBody: string | undefined;
	let previousBody: string | undefined;
	let phase: AtAGlanceRequestPhase = 'beforeAttempt';
	let inputObserved = false;
	let input: AtAGlanceObservedInput = {
		requestBody: null,
		waitingOnPrereqs: false,
		enabled: false,
		completedMatch: false,
	};

	const reuse = createAtAGlanceReuseObservation();
	const decision = (): AtAGlanceBrowserDecision => {
		if (!inputObserved) {
			return 'unknown';
		}
		if (!input.enabled) {
			return 'unableToRequest';
		}
		if (input.requestBody && activeBody === input.requestBody) {
			return 'pendingMatch';
		}
		if (input.requestBody && input.completedMatch) {
			return 'completedMatch';
		}
		if (activeBody && input.requestBody) {
			return 'unknown';
		}
		if (input.waitingOnPrereqs) {
			return 'waitingForInput';
		}
		return input.requestBody ? 'unknown' : 'unableToRequest';
	};
	const report = (
		attributes: Omit<AtAGlanceBrowserObservation, 'visitId' | 'browserTimeMs'>,
		operationBody?: string,
	) => {
		if (ended) {
			return;
		}
		try {
			const browserTimeMs = now();
			if (!Number.isFinite(browserTimeMs) || browserTimeMs < 0) {
				return;
			}
			visitId ??= createId();
			let summaryStartTrigger: AtAGlanceSummaryStartTrigger | undefined;
			const trigger = getSummaryStartTrigger?.();
			if (trigger === 'panel' || trigger === 'popup') {
				summaryStartTrigger = trigger;
			}
			emit({
				visitId,
				browserTimeMs,
				firstPopupOpenedAtMs,
				firstAttemptStartedAtMs,
				lastResponseCompletedAtMs,
				...(summaryStartTrigger ? { summaryStartTrigger } : {}),
				...attributes,
				...reuse.fields(input.requestBody, () => browserTimeMs, operationBody),
			});
		} catch {
			// Observation delivery must never interrupt paid work, cancellation, or popup actions.
		}
	};
	const getSnapshot = (): AtAGlanceObservationSnapshot => ({
		visitId,
		popupOpeningCount,
		firstPopupOpenedAtMs,
		firstAttemptStartedAtMs,
		lastResponseCompletedAtMs,
		phase,
		decision: decision(),
		...reuse.fields(input.requestBody, now),
	});
	const panel = createAtAGlancePanelObservation({ now, report, getSnapshot });
	return {
		observePanel: (open) => {
			if (!ended) {
				panel.observePanel(open);
			}
		},
		observeResult: (result) => {
			if (!ended) {
				reuse.observeResult(result.acceptedResult);
				panel.observeResult(result);
			}
		},
		observeOverviewCommit: (acceptedResult) => {
			if (!ended) {
				panel.observeOverviewCommit(reuse.captureOverviewCommit(acceptedResult));
			}
		},
		observePopup: (open) => {
			if (ended) {
				return;
			}
			if (open && !popupOpen) {
				popupOpeningCount += 1;
				firstPopupOpenedAtMs ??= now();
				pendingPopupOperationIndex = operationIndex;
				report({ stage: 'popupOpened', popupOpeningIndex: popupOpeningCount, phase });
			}
			popupOpen = open;
		},
		resolvePopupDecision: () => {
			if (pendingPopupOperationIndex === undefined) {
				return;
			}
			const actualDecision =
				operationIndex > pendingPopupOperationIndex ? 'newOperation' : decision();
			report({
				stage: 'popupDecision',
				popupOpeningIndex: popupOpeningCount,
				decision: actualDecision,
				phase,
			});
			pendingPopupOperationIndex = undefined;
		},
		observeInput: (next) => {
			inputObserved = true;
			input = next;
			panel.observeDocument(next.documentOutcome, next.documentReady);
		},
		startOperation: (requestBody) => {
			const index = ++operationIndex;
			let replacementInput: AtAGlanceBrowserObservation['replacementInput'];
			if (previousBody === undefined) {
				replacementInput = 'firstOperation';
			} else if (previousBody === requestBody) {
				replacementInput = 'identical';
			} else {
				replacementInput = 'changed';
			}
			previousBody = requestBody;
			activeBody = requestBody;
			phase = 'beforeAttempt';
			return (event) => {
				if (ended) {
					return;
				}
				if (event.stage === 'attemptStarted') {
					firstAttemptStartedAtMs ??= now();
				}
				if (event.stage === 'attemptFinished' && event.statusCode !== undefined) {
					lastResponseCompletedAtMs = now();
				}
				if (index === operationIndex) {
					phase = event.stage === 'operationFinished' ? 'settled' : event.phase;
					if (event.stage === 'operationFinished') {
						activeBody = undefined;
					}
				}
				report({ ...event, operationIndex: index, replacementInput }, requestBody);
				if (index === operationIndex) {
					panel.refresh();
				}
			};
		},
		getSnapshot,
		getCancellationReason: (latestInput = input) => {
			const lifecycleReason = getVisitEndReason();
			if (lifecycleReason !== 'unknown') {
				return lifecycleReason;
			}
			if (latestInput.waitingOnPrereqs && !latestInput.requestBody) {
				return 'temporaryInputLoading';
			}
			if (latestInput.requestBody && activeBody && latestInput.requestBody !== activeBody) {
				return 'changedInput';
			}
			return 'unknown';
		},
		endVisit: () => {
			panel.close();
			if (visitId) {
				report({
					stage: 'visitEnded',
					reason: getVisitEndReason(),
					popupOpeningIndex: popupOpeningCount,
					decision: decision(),
					phase,
				});
			}
			ended = true;
		},
	};
};
